import type { PortableTextBlock } from '@portabletext/types';

import { VIDEO_SLOT_ID, planIncontentSlots } from '@lib/ads/snack-slots';
import type { BodyBlockKind, IncontentSlotId } from '@lib/ads/snack-slots';

export const AD_SLOT_BLOCK_TYPE = 'snackAdSlot';
export const VIDEO_SLOT_BLOCK_TYPE = 'snackVideoSlot';

export interface AdSlotBlock {
  _type: typeof AD_SLOT_BLOCK_TYPE;
  _key: string;
  slotId: IncontentSlotId;
}

export interface VideoSlotBlock {
  _type: typeof VIDEO_SLOT_BLOCK_TYPE;
  _key: string;
}

const PARAGRAPH_STYLE = 'normal';

const hasWords = (block: PortableTextBlock): boolean =>
  (block.children ?? []).some(
    (child) => 'text' in child && String(child.text).trim() !== ''
  );

const getBlockKind = (block: PortableTextBlock): BodyBlockKind => {
  if (block._type !== 'block') return 'other';
  if (block.listItem) return 'text';
  if ((block.style ?? PARAGRAPH_STYLE) !== PARAGRAPH_STYLE) return 'text';
  return hasWords(block) ? 'paragraph' : 'text';
};

/**
 * An article body with Snack's in-content ad slots (FFH-3481) and video
 * player (FFH-3483) placed in it, rendered by the snackAdSlot and
 * snackVideoSlot types in portable-text-components.
 *
 * Each slot's key comes from the block before it, so moving to another article
 * mounts fresh slot elements: Snack's wrapper only fills slots it sees being
 * added to the page, so reused ones would keep the last article's ads.
 */
export const withIncontentAdSlots = (
  body: PortableTextBlock[]
): (PortableTextBlock | AdSlotBlock | VideoSlotBlock)[] => {
  const slots = planIncontentSlots(body.map(getBlockKind), {
    shouldPlaceVideo: true
  });
  if (slots.length === 0) return body;

  const slotIdsByIndex = new Map(
    slots.map(({ afterIndex, slotId }) => [afterIndex, slotId])
  );

  return body.flatMap((block, index) => {
    const slotId = slotIdsByIndex.get(index);
    if (!slotId) return [block];

    const _key = `${block._key ?? index}-${slotId}`;
    const slot: AdSlotBlock | VideoSlotBlock =
      slotId === VIDEO_SLOT_ID
        ? { _type: VIDEO_SLOT_BLOCK_TYPE, _key }
        : { _type: AD_SLOT_BLOCK_TYPE, _key, slotId };
    return [block, slot];
  });
};
